import React, { useState, useRef } from 'react';
import {
  FormControl,
  Input,
  InputAdornment,
  IconButton,
  FormHelperText,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const MASKED_CHAR = '\u2022'; // • Bullet character

function PasswordInput({
  onPasswordChange,
  label = 'Password',
  name,
  onBlur,
  formErrors = {},
  isUserLoggedIn = false,
  inputCommonProps = {},
  disableUnderline = false,

}) {
  const [realPassword, setRealPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const updatePassword = (newValue) => {
    setRealPassword(newValue);
    if (onPasswordChange) {
      onPasswordChange({ name, value: newValue });
    }
  };

  const setCaretPosition = (pos) => {
    const input = inputRef.current;
    input.setSelectionRange(pos, pos);
  };

  const handleKeyDown = (e) => {
    const input = inputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;

    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return; // Allow shortcuts
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      if (start === end && start > 0) {
        const updated = realPassword.slice(0, start - 1) + realPassword.slice(end);
        updatePassword(updated);
        setTimeout(() => setCaretPosition(start - 1), 0);
      } else if (start !== end) {
        const updated = realPassword.slice(0, start) + realPassword.slice(end);
        updatePassword(updated);
        setTimeout(() => setCaretPosition(start), 0);
      }
    } else if (e.key === 'Delete') {
      e.preventDefault();
      if (start !== end) {
        const updated = realPassword.slice(0, start) + realPassword.slice(end);
        updatePassword(updated);
        setTimeout(() => setCaretPosition(start), 0);
      } else if (start < realPassword.length) {
        const updated = realPassword.slice(0, start) + realPassword.slice(start + 1);
        updatePassword(updated);
        setTimeout(() => setCaretPosition(start), 0);
      }
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      const inserted = e.key;
      const updated = realPassword.slice(0, start) + inserted + realPassword.slice(end);
      updatePassword(updated);
      setTimeout(() => setCaretPosition(start + 1), 0);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const input = inputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;

    const updated = realPassword.slice(0, start) + pasted + realPassword.slice(end);
    updatePassword(updated);
    setTimeout(() => setCaretPosition(start + pasted.length), 0);
  };

  return (
    <FormControl
      variant="standard"
      className="form-control password-field"
      error={!!formErrors[name]}
    >
      <label>{label} *</label>
      <Input
        inputRef={inputRef}
        name={name}
        type="text"
        value={showPassword ? realPassword : MASKED_CHAR.repeat(realPassword.length)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={onBlur}
        disableUnderline={disableUnderline}
        disabled={isUserLoggedIn}
        autoComplete="off"
        inputProps={{
          ...inputCommonProps,
        }}
        endAdornment={
          <InputAdornment
            position="end"
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%) translateX(-80%)',
            }}
          >
            <IconButton
              onClick={handleClickShowPassword}
              edge="end"
              style={{ padding: '4px' }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
      <FormHelperText>{formErrors[name]}</FormHelperText>
    </FormControl>
  );
}

export default PasswordInput;
